import { ArrowLeft } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import { useInView } from 'react-intersection-observer'

import { useTranslate } from '@orus.eu/pharaoh'
import { Button } from '@orus.eu/pharaoh-next/components/ui/button'

import { PharaohNextScope } from '#/lib/pharaoh-next/pharaoh-next-scope.tsx'

/**
 * The funnel's mobile navigation bar: back, then the next button across the rest of the row. It sticks to the bottom of
 * the scrolling page, and settles in place at the end of the content, so it never hides the last answer.
 */
export function StandaloneFormMobileNavigationBar({
  onBack,
  backDisabled,
  children,
}: {
  /** Undefined hides the back button. */
  onBack: (() => void) | undefined
  backDisabled: boolean
  /** The next button. */
  children: ReactNode
}): ReactElement {
  const translate = useTranslate()
  // The marker sits where the bar settles: while it is out of view, content passes under the bar
  const { ref: settledMarkerRef, inView: settled } = useInView({ threshold: 1 })

  return (
    <>
      <div ref={settledMarkerRef} className="mt-auto" />
      {/* Sticky outside the scope, whose wrapper would otherwise be the only area it sticks within */}
      <div className="sticky bottom-0 z-10">
        <PharaohNextScope>
          <div
            data-settled={settled ? '' : undefined}
            className="
              flex gap-30 rounded-t-30 bg-surface-base-normal px-60 pt-50
              pb-[calc(var(--spacing-50)+env(safe-area-inset-bottom))] shadow-top-20
              data-settled:shadow-none
            "
          >
            {onBack ? (
              <Button
                variant="secondary"
                size="icon-medium"
                mobileSize="icon-large"
                aria-label={translate('back')}
                disabled={backDisabled}
                onClick={onBack}
              >
                <ArrowLeft />
              </Button>
            ) : null}
            <div className="grid flex-1">{children}</div>
          </div>
        </PharaohNextScope>
      </div>
    </>
  )
}
